import { State } from 'types';
import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';
import { DateTime } from 'luxon';

type GetAvailabilitiesProps = {
  appointmentTypeId: number;
  physicianId?: number;
  limit?: number;
  startDate?: string;
  startTime?: string;
  endDate?: string;
};

export type AvailabilityDates = {
  [key: string]: string[];
};

export const getAvailabilities = (state: State) => state.entities.availabilities;

const _getAvailabilitiesAppointmentTypeId = (_state: State, options: GetAvailabilitiesProps) =>
  options.appointmentTypeId;

const _getAvailabilitiesPhysicianIds = (_state: State, options: GetAvailabilitiesProps) =>
  options.physicianId;

const _getAvailabilitiesLimit = (_state: State, options: GetAvailabilitiesProps) => options.limit;

const _getAvailabilitiesStartDate = (_state: State, options: GetAvailabilitiesProps) =>
  options.startDate || DateTime.fromObject({}).toISODate();

const _getAvailabilitiesStartTime = (_state: State, options: GetAvailabilitiesProps) =>
  options.startTime;

const _getAvailabilitiesEndDate = (_state: State, options: GetAvailabilitiesProps) =>
  options.endDate;

const _getAvailabilitiesForAppointmentType = createSelector(
  getAvailabilities,
  _getAvailabilitiesAppointmentTypeId,
  (availabilities, appointmentTypeId) =>
    availabilities ? availabilities[appointmentTypeId] : undefined,
);

export const getAvailabilitiesForAppointmentType = createSelector(
  _getAvailabilitiesForAppointmentType,
  _getAvailabilitiesPhysicianIds,
  _getAvailabilitiesLimit,
  _getAvailabilitiesStartDate,
  _getAvailabilitiesEndDate,
  _getAvailabilitiesStartTime,
  (availabilities, physicianId, limit, startDate, endDate, startTime) => {
    if (!availabilities) {
      return undefined;
    }
    const dates: AvailabilityDates = {};

    Object.keys(availabilities)
      .sort()
      .filter((date) => (endDate ? date >= startDate && date <= endDate : date >= startDate))
      .forEach((date) => {
        const times = availabilities[date];
        let timesArray = Object.keys(times);

        if (startTime && date === startDate) {
          timesArray = timesArray.filter((time) => time > startTime);
        }

        if (physicianId) {
          timesArray = timesArray.filter((time) => times[time].includes(physicianId));
        }

        if (timesArray.length) {
          dates[date] = timesArray.sort();
        }
      });

    if (isEmpty(dates)) {
      return undefined;
    }

    if (limit) {
      let totalCount = 0;
      const limitedDates: AvailabilityDates = {};
      const datesMap = new Map(Object.entries(dates));

      // eslint-disable-next-line no-restricted-syntax
      for (const [date, times] of datesMap.entries()) {
        // eslint-disable-next-line no-restricted-syntax
        for (const time of times as string[]) {
          if (totalCount >= limit) {
            break;
          }
          limitedDates[date] = limitedDates[date] ? [...limitedDates[date], time] : [time];
          totalCount++;
        }
      }
      return limitedDates;
    }

    return dates;
  },
);
